import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";

// ✅ ปรับ path ให้ตรงโปรเจกต์ของคุณ
import { db, auth, storage } from "../../../../shared/libs/firebaseConfig";

const PAGE_SIZE = 100;

function toDateSafe(value) {
  try {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === "number") return new Date(value);
    if (typeof value === "string") return new Date(value);
    return null;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const d = toDateSafe(value);
  if (!d) return "-";
  return new Intl.DateTimeFormat("th-TH", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

function formatCurrency(value) {
  const num = Number(value || 0);
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    maximumFractionDigits: 0,
  }).format(num);
}

function chunkArray(arr, size = 10) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

/* function getUsernameText(item) {
  const username = item?.userSnapshot?.username;
  const fullName = item?.userSnapshot?.fullName;
  const email = item?.userSnapshot?.email;

  if (username && String(username).trim()) return username;
  if (fullName && String(fullName).trim()) return fullName;
  if (email && String(email).includes("@")) return String(email).split("@")[0];
  return "ไม่ระบุชื่อผู้สมัคร";
} */
function getUsernameText(item) {
  const email = item?.userSnapshot?.email;
  const fullName = item?.userSnapshot?.fullName;

  if (email && String(email).trim()) return email;
  if (fullName && String(fullName).trim()) return fullName;
  return "ไม่ระบุอีเมล";
}

function getStatusMeta(item) {
  const accessStatus = item?.accessStatus || "";
  const paymentStatus = item?.paymentStatus || "";

  if (accessStatus === "granted") {
    return { label: "อนุมัติแล้ว", variant: "success" };
  }

  if (accessStatus === "revoked" || accessStatus === "suspended") {
    return { label: "ระงับสิทธิ์", variant: "danger" };
  }

  if (paymentStatus === "paid" && accessStatus !== "granted") {
    return { label: "ชำระเงินแล้ว / รอตรวจสอบ", variant: "warning" };
  }

  return { label: "รอการชำระเงิน", variant: "secondary" };
}

function matchesSearch(item, keyword) {
  if (!keyword) return true;
  const k = keyword.trim().toLowerCase();
  if (!k) return true;

  const installmentRaw = (Array.isArray(item?.installments) ? item.installments : [])
    .map((inst) =>
      [
        inst?.installmentNo,
        inst?.amount,
        inst?.status,
        inst?.paymentMethod,
        inst?.paymentChannel,
      ]
        .filter(Boolean)
        .join(" ")
    )
    .join(" ");

  const raw = [
    item?.userSnapshot?.username,
    item?.userSnapshot?.fullName,
    item?.userSnapshot?.email,
    item?.userSnapshot?.phoneNumber,
    item?.userSnapshot?.shippingAddress,
    item?.courseTitle,
    item?.courseSnapshot?.title,
    item?.courseType,
    item?.paymentType,
    item?.paymentStatus,
    item?.accessStatus,
    item?.verifiedTransactionRef,
    item?.paymentMethod,
    item?.paymentChannel,
    item?.slipVerifiedData?.data?.sender?.account?.name,
    item?.slipVerifiedData?.data?.sender?.account?.bank?.name,
    installmentRaw,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return raw.includes(k);
}

export default function CreatorEnrollments() {
  const [uid, setUid] = useState(null);

  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  const [enrolls, setEnrolls] = useState([]);
  const [enrollsLoading, setEnrollsLoading] = useState(true);

  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [successText, setSuccessText] = useState("");

  const [searchText, setSearchText] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [daysFilter, setDaysFilter] = useState("all");

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [slipUrlMap, setSlipUrlMap] = useState({});
  const [openingSlipId, setOpeningSlipId] = useState("");
  const [updatingId, setUpdatingId] = useState("");

  const enrollUnsubsRef = useRef([]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid || null);
    });
    return unsub;
  }, []);

  // โหลดคอร์สของ creator
  useEffect(() => {
    if (!uid) {
      setCourses([]);
      setCoursesLoading(false);
      return;
    }

    setCoursesLoading(true);
    setError("");

    const q = query(
      collection(db, "courses"),
      where("ownerUid", "==", uid),
      orderBy("updatedAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setCourses(list);
        setCoursesLoading(false);
      },
      (err) => {
        console.error(err);
        setError(err?.message || "ไม่สามารถโหลดรายการคอร์สได้");
        setCoursesLoading(false);
      }
    );

    return unsub;
  }, [uid]);

  // subscribe enrolls แบบ realtime
  useEffect(() => {
    enrollUnsubsRef.current.forEach((fn) => {
      try {
        fn();
      } catch {}
    });
    enrollUnsubsRef.current = [];

    setEnrolls([]);
    setEnrollsLoading(true);
    setError("");

    if (!uid || coursesLoading) {
      setEnrollsLoading(false);
      return;
    }

    const ownCourseIds = courses.map((c) => c.id);
    if (!ownCourseIds.length) {
      setEnrolls([]);
      setEnrollsLoading(false);
      return;
    }

    const activeCourseIds = selectedCourseId
      ? [selectedCourseId]
      : ownCourseIds.slice();

    const courseChunks = chunkArray(activeCourseIds, 10);

    const startDate =
      daysFilter === "all"
        ? null
        : Timestamp.fromDate(
            new Date(Date.now() - Number(daysFilter) * 24 * 60 * 60 * 1000)
          );

    const mergedMap = new Map();
    let readyCount = 0;
    const totalListeners = courseChunks.length;

    courseChunks.forEach((ids) => {
      let qRef;

      if (startDate) {
        qRef = query(
          collection(db, "enrolls"),
          where("courseId", "in", ids),
          where("createdAt", ">=", startDate),
          orderBy("createdAt", "desc")
        );
      } else {
        qRef = query(
          collection(db, "enrolls"),
          where("courseId", "in", ids),
          orderBy("createdAt", "desc")
        );
      }

      const unsub = onSnapshot(
        qRef,
        (snap) => {
          // ลบ docs ของ chunk เดิมก่อน แล้วใส่ของใหม่กลับเข้าไป
          const idSet = new Set(ids);

          for (const [key, value] of mergedMap.entries()) {
            if (idSet.has(value.courseId)) {
              mergedMap.delete(key);
            }
          }

          snap.docs.forEach((d) => {
            mergedMap.set(d.id, { id: d.id, ...d.data() });
          });

          readyCount += 1;

          const mergedList = Array.from(mergedMap.values()).sort((a, b) => {
            const ad = toDateSafe(a.createdAt)?.getTime() || 0;
            const bd = toDateSafe(b.createdAt)?.getTime() || 0;
            return bd - ad;
          });

          setEnrolls(mergedList);

          if (readyCount >= totalListeners) {
            setEnrollsLoading(false);
          }
        },
        (err) => {
          console.error(err);
          setError(err?.message || "ไม่สามารถโหลดข้อมูลผู้สมัครได้");
          setEnrollsLoading(false);
        }
      );

      enrollUnsubsRef.current.push(unsub);
    });

    return () => {
      enrollUnsubsRef.current.forEach((fn) => {
        try {
          fn();
        } catch {}
      });
      enrollUnsubsRef.current = [];
    };
  }, [uid, courses, coursesLoading, selectedCourseId, daysFilter]);

  // reset จำนวนแสดงผลเมื่อ filter เปลี่ยน
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchText, selectedCourseId, statusFilter, daysFilter]);

  /* const filteredEnrolls = useMemo(() => {
    let list = enrolls.slice();

    if (statusFilter === "granted") {
      list = list.filter((item) => item?.accessStatus === "granted");
    } else if (statusFilter === "waiting") {
      list = list.filter((item) => item?.accessStatus !== "granted");
    }

    list = list.filter((item) => matchesSearch(item, searchText));

    return list;
  }, [enrolls, statusFilter, searchText]); */
  const filteredEnrolls = useMemo(() => {
    let list = enrolls.slice();

    if (statusFilter === "granted") {
      list = list.filter((item) => item?.accessStatus === "granted");
    } else if (statusFilter === "waiting") {
      list = list.filter((item) => item?.accessStatus !== "granted");
    } else if (statusFilter === "installment") {
      list = list.filter((item) => item?.paymentType === "installment");
    }

    list = list.filter((item) => matchesSearch(item, searchText));

    return list;
  }, [enrolls, statusFilter, searchText]);

  const visibleEnrolls = useMemo(() => {
    return filteredEnrolls.slice(0, visibleCount);
  }, [filteredEnrolls, visibleCount]);

  /* const summary = useMemo(() => {
    const applicantCount = filteredEnrolls.length;

    const totalSales = filteredEnrolls.reduce((sum, item) => {
      const isPaid = item?.paymentStatus === "paid" || item?.accessStatus === "granted";
      if (!isPaid) return sum;
      return sum + Number(item?.amount || item?.courseSnapshot?.price || 0);
    }, 0);

    return {
      applicantCount,
      totalSales,
    };
  }, [filteredEnrolls]); */
  const summary = useMemo(() => {
    const applicantCount = filteredEnrolls.length;

    const totalSales = filteredEnrolls.reduce((sum, item) => {
      return sum + getActuallyPaidAmount(item);
    }, 0);

    return {
      applicantCount,
      totalSales,
    };
  }, [filteredEnrolls]);

  async function handleOpenSlip(item) {
    try {
      if (!item?.id || !item?.slipImagePath) return;

      setOpeningSlipId(item.id);

      if (slipUrlMap[item.id]) {
        window.open(slipUrlMap[item.id], "_blank", "noopener,noreferrer");
        return;
      }

      const url = await getDownloadURL(storageRef(storage, item.slipImagePath));

      setSlipUrlMap((prev) => ({
        ...prev,
        [item.id]: url,
      }));

      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      setActionError(err?.message || "ไม่สามารถเปิดดูไฟล์สลิปได้");
    } finally {
      setOpeningSlipId("");
    }
  }

  async function handleOpenInstallmentSlip(item, installmentNo) {
    try {
      if (!item?.id) return;

      const installments = Array.isArray(item?.installments) ? item.installments : [];
      const installment = installments.find(
        (x) => Number(x?.installmentNo || 0) === Number(installmentNo || 0)
      );

      if (!installment?.slipImagePath) return;

      const cacheKey = `${item.id}_installment_${installmentNo}`;
      setOpeningSlipId(cacheKey);

      if (slipUrlMap[cacheKey]) {
        window.open(slipUrlMap[cacheKey], "_blank", "noopener,noreferrer");
        return;
      }

      const url = await getDownloadURL(
        storageRef(storage, installment.slipImagePath)
      );

      setSlipUrlMap((prev) => ({
        ...prev,
        [cacheKey]: url,
      }));

      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      setActionError(err?.message || "ไม่สามารถเปิดดูไฟล์สลิปงวดผ่อนชำระได้");
    } finally {
      setOpeningSlipId("");
    }
  }

  async function handleChangeAccessStatus(enrollId, nextStatus) {
    try {
      setUpdatingId(enrollId);
      setActionError("");
      setSuccessText("");

      const payload = {
        accessStatus: nextStatus,
        updatedAt: Timestamp.now(),
      };

      // ✅ กรณีอนุมัติสิทธิ์แบบ manual
      if (nextStatus === "granted") {
        payload.paymentStatus = "paid";
        payload.status = "active";
        payload.paidAt = Timestamp.now();
        payload.accessGrantedAt = Timestamp.now();

        // ระบุแหล่งที่มาเพื่อให้ดูย้อนหลังได้
        payload.paymentMethod = "manual_review";
        payload.paymentChannel = "creator_manual_approval";
        payload.verifiedBy = "creator_manual";
        payload.verificationSource = "creator_panel";
      }

      // ⏳ กรณีกลับไป pending
      if (nextStatus === "pending") {
        payload.status = "pending_payment";
        payload.paymentStatus = "pending";
      }

      // ⛔ กรณีระงับสิทธิ์
      if (nextStatus === "revoked") {
        payload.status = "inactive";
        payload.accessStatus = "revoked";
      }

      await updateDoc(doc(db, "enrolls", enrollId), payload);

      setSuccessText("อัปเดตสถานะการเข้าเรียนเรียบร้อยแล้ว");
    } catch (err) {
      console.error(err);
      setActionError(err?.message || "อัปเดตสถานะไม่สำเร็จ");
    } finally {
      setUpdatingId("");
    }
  }

  function isPerfectPackage(item) {
    return String(item?.coursePackage || "").startsWith("P_");
  }

  function getPackageDisplayText(item) {
    return item?.coursePackageLabel || item?.coursePackage || "-";
  }

  function getInstallmentList(item) {
  return Array.isArray(item?.installments) ? item.installments : [];
}

function isInstallmentPayment(item) {
  return item?.paymentType === "installment";
}

function getPaidInstallmentCount(item) {
  return getInstallmentList(item).filter((x) => x?.status === "paid").length;
}

function getTotalInstallmentCount(item) {
  return (
    getInstallmentList(item).length ||
    Number(item?.installmentPlan?.totalInstallments || 0)
  );
}

function getActuallyPaidAmount(item) {
  if (item?.paymentType === "installment") {
    const installments = getInstallmentList(item);
    return installments.reduce((sum, inst) => {
      if (inst?.status !== "paid") return sum;
      return sum + Number(inst?.amount || 0);
    }, 0);
  }

  const isPaid =
    item?.paymentStatus === "paid" || item?.accessStatus === "granted";

  if (!isPaid) return 0;

  return Number(item?.amount || item?.courseSnapshot?.price || 0);
}

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h3 className="mb-1">
            <strong>ผู้สมัครเรียน</strong>
          </h3>
          <div className="text-muted">
            สำหรับ creator จะแสดงเฉพาะคอร์สที่คุณเป็นผู้สร้าง
          </div>
        </div>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {actionError ? <Alert variant="danger">{actionError}</Alert> : null}
      {successText ? <Alert variant="success">{successText}</Alert> : null}

      {/* ฟิลเตอร์ */}
      <Card className="shadow-sm border-0 mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} lg={4}>
              <Form.Label>ค้นหาผู้สมัคร</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="ชื่อ, อีเมล, เบอร์, ที่อยู่, คอร์ส"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </InputGroup>
            </Col>

            <Col xs={12} md={6} lg={3}>
              <Form.Label>เลือกคอร์ส</Form.Label>
              <Form.Select
                value={selectedCourseId}
                onChange={(e) => setSelectedCourseId(e.target.value)}
                disabled={coursesLoading}
              >
                <option value="">ทุกคอร์สของฉัน</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title || "(ไม่มีชื่อคอร์ส)"}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={2}>
              <Form.Label>สถานะ</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">ทั้งหมด</option>
                <option value="granted">อนุมัติแล้ว</option>
                <option value="waiting">รอการชำระเงิน / รอตรวจสอบ</option>
                <option value="installment">ผ่อนชำระ</option>
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={2}>
              <Form.Label>จำนวนวัน</Form.Label>
              <Form.Select
                value={daysFilter}
                onChange={(e) => setDaysFilter(e.target.value)}
              >
                <option value="all">ทั้งหมด</option>
                <option value="7">7 วัน</option>
                <option value="14">14 วัน</option>
                <option value="30">30 วัน</option>
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={1} className="d-flex align-items-end">
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() => {
                  setSearchText("");
                  setSelectedCourseId("");
                  setStatusFilter("all");
                  setDaysFilter("all");
                }}
              >
                ล้าง
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* สรุปยอด */}
      <Row className="g-3 mb-3">
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="text-muted mb-1">จำนวนผู้สมัคร</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {summary.applicantCount.toLocaleString("th-TH")}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="text-muted mb-1">ยอดขายรวม</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {formatCurrency(summary.totalSales)}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* รายการ */}
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <strong>รายการผู้สมัครล่าสุด</strong>
            </div>
            <div className="text-muted small">
              แสดง {Math.min(visibleEnrolls.length, filteredEnrolls.length)} /{" "}
              {filteredEnrolls.length.toLocaleString("th-TH")} รายการ
            </div>
          </div>

          {coursesLoading || enrollsLoading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" />
              <div className="mt-2 text-muted">กำลังโหลดข้อมูล...</div>
            </div>
          ) : filteredEnrolls.length === 0 ? (
            <Alert variant="light" className="mb-0 border">
              ไม่พบข้อมูลผู้สมัครตามเงื่อนไขที่เลือก
            </Alert>
          ) : (
            <>
              <div className="d-flex flex-column gap-3">
                {visibleEnrolls.map((item) => {
                  const statusMeta = getStatusMeta(item);
                  const usernameText = getUsernameText(item);

                  return (
                    <Card key={item.id} className="border">
                      <Card.Body>
                        <Row className="g-3">
                          <Col xs={12} lg={8}>
                            <div className="mb-2">
                              {/* <div className="d-flex flex-wrap align-items-center gap-2">
                                <div style={{ fontSize: 18, fontWeight: 700 }}>
                                  {usernameText}
                                </div>
                                <Badge bg={statusMeta.variant}>{statusMeta.label}</Badge>
                                <Badge bg="light" text="dark">
                                  {item?.courseTitle || item?.courseSnapshot?.title || "-"}
                                </Badge>
                              </div> */}
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                <div style={{ fontSize: 18, fontWeight: 700 }}>
                                  {usernameText}
                                </div>

                                <Badge bg={statusMeta.variant}>{statusMeta.label}</Badge>

                                <Badge bg="light" text="dark">
                                  {item?.courseTitle || item?.courseSnapshot?.title || "-"}
                                </Badge>

                                <span
                                  style={{
                                    fontSize: 12,
                                    color: "#9ca3af",
                                    background: "#f3f4f6",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 999,
                                    padding: "2px 8px",
                                    userSelect: "all",
                                  }}
                                  title="enroll docId"
                                >
                                  enrollId: {item.id}
                                </span>
                              </div>

                              {item?.coursePackage ? (
                                <div
                                  className={`mt-1 fw-semibold ${
                                    isPerfectPackage(item) ? "text-primary" : "text-muted"
                                  }`}
                                >
                                  แพ็กเกจ: {getPackageDisplayText(item)}
                                </div>
                              ) : null}
                            </div>

                            <div className="mb-1">
                              <strong>ชื่อ-นามสกุล:</strong>{" "}
                              {item?.userSnapshot?.fullName || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>อีเมล:</strong>{" "}
                              {item?.userSnapshot?.email || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>เบอร์โทรศัพท์:</strong>{" "}
                              {item?.userSnapshot?.phoneNumber || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>ที่อยู่:</strong>{" "}
                              {item?.userSnapshot?.shippingAddress || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>ยอดชำระ:</strong>{" "}
                              {item?.amountText ||
                                item?.courseSnapshot?.priceText ||
                                formatCurrency(item?.amount || item?.courseSnapshot?.price || 0)}
                            </div>

                            <div className="mb-1">
                              <strong>ประเภทการชำระเงิน:</strong>{" "}
                              {item?.paymentType === "installment" ? "ผ่อนชำระ" : "ชำระเต็มจำนวน"}
                            </div>

                            <div className="mb-1">
                              <strong>ยอดที่รับเงินจริงแล้ว:</strong>{" "}
                              {formatCurrency(getActuallyPaidAmount(item))}
                            </div>

                            {isInstallmentPayment(item) ? (
                              <div className="mb-2">
                                <strong>แผนผ่อนชำระ:</strong>{" "}
                                {getPaidInstallmentCount(item)}/{getTotalInstallmentCount(item)} งวด
                                {item?.installmentPlan?.status ? (
                                  <span className="ms-2 text-muted">
                                    ({item.installmentPlan.status})
                                  </span>
                                ) : null}
                              </div>
                            ) : null}

                            <div className="mb-1">
                              <strong>วิธีชำระเงิน:</strong>{" "}
                              {item?.paymentMethod || "-"} / {item?.paymentChannel || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>สถานะการชำระเงิน:</strong>{" "}
                              {item?.paymentStatus || "-"}
                            </div>

                            <div className="mb-1">
                              <strong>สมัครเมื่อ:</strong>{" "}
                              {formatDateTime(item?.createdAt)}
                            </div>

                            <div className="mb-1">
                              <strong>ชำระเมื่อ:</strong>{" "}
                              {formatDateTime(item?.paidAt)}
                            </div>

                            <div className="mb-1">
                              <strong>อนุมัติสิทธิ์เมื่อ:</strong>{" "}
                              {formatDateTime(item?.accessGrantedAt)}
                            </div>

                            {item?.verifiedReceiver?.accountName ? (
                              <div className="mb-1">
                                <strong>ผู้รับเงินที่ตรวจพบ:</strong>{" "}
                                {item.verifiedReceiver.accountName}
                              </div>
                            ) : null}

                            {item?.verifiedTransactionRef ? (
                              <div className="mb-1">
                                <strong>Transaction Ref:</strong>{" "}
                                {item.verifiedTransactionRef}
                              </div>
                            ) : null}

                            {isInstallmentPayment(item) && getInstallmentList(item).length > 0 ? (
                              <div className="mt-3">
                                <div className="fw-semibold mb-2">รายการงวดผ่อนชำระ</div>

                                <div className="d-flex flex-column gap-2">
                                  {getInstallmentList(item).map((inst) => {
                                    const slipKey = `${item.id}_installment_${inst.installmentNo}`;
                                    const isOpeningThisSlip = openingSlipId === slipKey;

                                    return (
                                      <div
                                        key={`${item.id}_inst_${inst.installmentNo}`}
                                        style={{
                                          border: "1px solid rgba(0,0,0,.08)",
                                          borderRadius: 10,
                                          padding: "10px 12px",
                                          background: "#fafafa",
                                        }}
                                      >
                                        <div className="d-flex flex-wrap justify-content-between gap-2">
                                          <div>
                                            <div className="fw-semibold">งวดที่ {inst.installmentNo}</div>
                                            <div className="small text-muted">
                                              ครบกำหนด: {formatDateTime(inst?.dueAt)}
                                            </div>
                                          </div>

                                          <div className="text-end">
                                            <div className="fw-semibold">{formatCurrency(inst?.amount || 0)}</div>
                                            <div className="small text-muted">
                                              สถานะ: {inst?.status || "-"}
                                            </div>
                                          </div>
                                        </div>

                                        <div className="small text-muted mt-2">
                                          ชำระเมื่อ: {formatDateTime(inst?.paidAt)}
                                        </div>

                                        <div className="mt-2">
                                          <Button
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={() => handleOpenInstallmentSlip(item, inst.installmentNo)}
                                            disabled={!inst?.slipImagePath || isOpeningThisSlip}
                                          >
                                            {isOpeningThisSlip ? (
                                              <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                กำลังเปิดสลิป...
                                              </>
                                            ) : inst?.slipImagePath ? (
                                              `เปิดดูสลิปงวดที่ ${inst.installmentNo}`
                                            ) : (
                                              "ไม่มีไฟล์สลิป"
                                            )}
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : null}
                          </Col>

                          <Col xs={12} lg={4}>
                            <Card className="bg-light border-0 h-100">
                              <Card.Body>
                                <div className="mb-2">
                                  <strong>จัดการสิทธิ์เข้าเรียน</strong>
                                </div>

                                <Form.Group className="mb-3">
                                  <Form.Label>สถานะสิทธิ์เข้าเรียน</Form.Label>
                                  <Form.Select
                                    value={item?.accessStatus || "pending"}
                                    disabled={updatingId === item.id}
                                    onChange={(e) =>
                                      handleChangeAccessStatus(item.id, e.target.value)
                                    }
                                  >
                                    <option value="pending">รอการชำระเงิน / รอตรวจสอบ</option>
                                    <option value="granted">อนุมัติสิทธิ์เข้าเรียน</option>
                                    <option value="revoked">ระงับสิทธิ์เข้าเรียน</option>
                                  </Form.Select>
                                </Form.Group>

                                <div className="d-grid gap-2">
                                  {/* <Button
                                    variant="outline-primary"
                                    onClick={() => handleOpenSlip(item)}
                                    disabled={
                                      !item?.slipImagePath || openingSlipId === item.id
                                    }
                                  >
                                    {openingSlipId === item.id ? (
                                      <>
                                        <Spinner
                                          animation="border"
                                          size="sm"
                                          className="me-2"
                                        />
                                        กำลังเปิดสลิป...
                                      </>
                                    ) : item?.slipImagePath ? (
                                      "เปิดดูสลิปโอนเงิน"
                                    ) : (
                                      "ไม่มีไฟล์สลิป"
                                    )}
                                  </Button> */}
                                  <Button
                                    variant="outline-primary"
                                    onClick={() => handleOpenSlip(item)}
                                    disabled={
                                      isInstallmentPayment(item)
                                        ? true
                                        : (!item?.slipImagePath || openingSlipId === item.id)
                                    }
                                  >
                                    {isInstallmentPayment(item)
                                      ? "ดูสลิปในแต่ละงวดด้านซ้าย"
                                      : openingSlipId === item.id ? (
                                          <>
                                            <Spinner
                                              animation="border"
                                              size="sm"
                                              className="me-2"
                                            />
                                            กำลังเปิดสลิป...
                                          </>
                                        ) : item?.slipImagePath ? (
                                          "เปิดดูสลิปโอนเงิน"
                                        ) : (
                                          "ไม่มีไฟล์สลิป"
                                        )}
                                  </Button>

                                  {updatingId === item.id ? (
                                    <div className="text-muted small">
                                      <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                      />
                                      กำลังอัปเดตสถานะ...
                                    </div>
                                  ) : null}
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>

              {visibleCount < filteredEnrolls.length ? (
                <div className="text-center mt-4">
                  <Button
                    variant="outline-dark"
                    onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                  >
                    แสดงเพิ่มเติม
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}